import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
  ISelectStoreOptions,
  IStoreContext,
  OnConfirmStoreChangeParams,
  StoreClosedReason,
  StorePermissions,
} from 'features/store/types';
import {
  curriedGetStoreStatusV1 as curriedGetStoreStatus,
  isMobileOrderingAvailable,
  isSameStoreAndServiceMode,
  mergeRestaurantData,
} from 'features/store/utils';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnce from 'hooks/use-effect-once';
import { flushEventQueue } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useUIContext } from 'state/ui';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import logger from 'utils/logger';
import { getLocalizedServiceModeCategory, isDelivery } from 'utils/service-mode';
import { useMemoAll } from 'utils/use-memo-all';

import { LOG_MESSAGE_STORE_CHANGE_ERROR, hasShownStoreChangeDialog } from './constants';
import { useGetStoreFn } from './hooks/use-get-store-fn';
import { useStore } from './hooks/use-store';
import { useStoreAvailabilityFns } from './hooks/use-store-availability-fns';
import { useStoreUtils } from './hooks/use-store-utils';

export const StoreContext = React.createContext<IStoreContext>({} as IStoreContext);
export const useStoreContext = () => useContext(StoreContext);

export const StoreProvider = ({ children }: IBaseProps) => {
  const getRestaurant = useGetStoreFn();

  const serviceMode = useServiceModeLive();

  // Keep a ref so callbacks can read the latest serviceMode without
  // being recreated on every change.  This prevents the entire
  // StoreContext (115+ consumers) from re-rendering whenever
  // serviceMode changes.
  const serviceModeRef = useRef(serviceMode);
  serviceModeRef.current = serviceMode;

  const [availableDayParts, setAvailableDayParts] = useState<IDayPartBoundary[] | undefined>();
  const {
    isOpenForDelivery,
    isOpenForPickup,
    isStoreAvailable: isStoreAvailableFn,
  } = useStoreAvailabilityFns();

  const {
    setIsStoreOrderUnavailableDialogOpen,
    isStoreOrderUnavailableDialogOpen,
  } = useUIContext();

  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });
  const validMobileOrderingEnvs = useMemo(() => restaurantsConfig.validMobileOrderingEnvs ?? [], [
    restaurantsConfig.validMobileOrderingEnvs,
  ]);

  const [
    onConfirmStoreChangeParams,
    setOnConfirmStoreChangeParams,
  ] = useState<OnConfirmStoreChangeParams | null>(null);
  const [isSelectingStore, setIsSelectingStore] = useState(false);

  const clearOnConfirmStoreChangeParams = useCallback(() => {
    setOnConfirmStoreChangeParams(null);
  }, []);

  const { resetStore, setStore, store, onConfirmStoreChange } = useStore();

  const [isFetchingStore, setIsFetchingStore] = useState(!!store?.number);

  const { fetchStore, email, prices, isPosDataLoading, refetchPosData } = useStoreUtils({
    store,
  });

  const { getStoreServiceModeStatuses } = useStoreAvailabilityFns();

  const getStoreStatusFlags = useCallback<IStoreContext['getStoreStatusFlags']>(
    (storeToCheck, selectedServiceMode) => {
      if (!storeToCheck?._id) {
        return {
          isStoreOpenAndAvailable: false,
          isStoreOpenAndUnavailable: false,
          isStoreClosed: false,
          noStoreSelected: true,
        };
      }
      const serviceModeStatuses = getStoreServiceModeStatuses(storeToCheck);
      const status = curriedGetStoreStatus({
        store: storeToCheck,
        serviceModeStatuses,
        selectedServiceMode,
      })();

      return {
        isStoreOpenAndAvailable: status === StorePermissions.OPEN_AND_AVAILABLE,
        isStoreOpenAndUnavailable: status === StorePermissions.OPEN_AND_UNAVAILABLE,
        isStoreClosed: status === StorePermissions.CLOSED,
        noStoreSelected: status === StorePermissions.NO_STORE_SELECTED,
      };
    },
    [getStoreServiceModeStatuses]
  );

  const {
    isStoreOpenAndAvailable,
    isStoreOpenAndUnavailable,
    isStoreClosed,
    noStoreSelected,
  } = useMemo(() => getStoreStatusFlags(store, serviceMode), [
    getStoreStatusFlags,
    store,
    serviceMode,
  ]);

  // If flags are enabled, refresh restaurant availability & poll for the selected store's availability to notify the user when the store becomes unavailable.
  const fetchAndSetStore = useCallback(
    async (storeId: string) => {
      setIsFetchingStore(true);
      try {
        const fetchedStore = await fetchStore(storeId);
        if (fetchedStore) {
          await setStore({ ...fetchedStore });
          setIsFetchingStore(false);
        } else {
          // If no store was returned, treat it as an error
          setIsFetchingStore(false);
          throw new Error('No store data returned');
        }
      } catch (error) {
        setIsFetchingStore(false);
        throw error; // Re-throw the error so the caller can handle it
      }
    },
    [fetchStore, setStore]
  );

  const [StoreChangeDialog, openStoreChangeDialog] = useDialogModal({
    onConfirm: onConfirmStoreChange,
    modalAppearanceEventMessage: 'Confirmation: Store change',
    showCancel: true,
  });

  const validateStore = useCallback(
    async (
      newStore: IRestaurant,
      selectedServiceMode: ServiceMode | null
    ): Promise<{ isValid: boolean; reason?: string }> => {
      const isDeliveryMode = isDelivery(selectedServiceMode);
      const isStoreOpen = isDeliveryMode ? isOpenForDelivery(newStore) : isOpenForPickup(newStore);

      if (!isStoreOpen) {
        const reason = isDeliveryMode
          ? StoreClosedReason.DELIVERY_HOURS_CLOSED
          : StoreClosedReason.PICKUP_HOURS_CLOSED;

        return { isValid: false, reason };
      }

      const rbiRestaurant = await getRestaurant(newStore.number ?? null);
      const isRestaurantPosAvailable = rbiRestaurant?.available;
      const hasMobileOrdering = isMobileOrderingAvailable(
        newStore as IRestaurant,
        validMobileOrderingEnvs
      );

      if (!isRestaurantPosAvailable || !hasMobileOrdering) {
        const reason = !isRestaurantPosAvailable
          ? StoreClosedReason.POS_UNAVAILABLE
          : StoreClosedReason.MOBILE_ORDERING_DISABLED;

        return {
          isValid: false,
          reason,
        };
      }

      // Check if store is open but unavailable
      if (isStoreOpenAndUnavailable) {
        return { isValid: false, reason: StoreClosedReason.HAS_MOBILE_ORDERING_UNAVAILABLE };
      }

      return { isValid: true };
    },
    [
      getRestaurant,
      isOpenForDelivery,
      isOpenForPickup,
      isStoreOpenAndUnavailable,
      validMobileOrderingEnvs,
    ]
  );

  const handleStoreError = useCallback(
    (reason: string | undefined, callback?: () => void) => {
      setIsStoreOrderUnavailableDialogOpen(true);
      callback?.();
      if (reason) {
        logger.warn({
          message: LOG_MESSAGE_STORE_CHANGE_ERROR,
          reason,
        });
      }
    },
    [setIsStoreOrderUnavailableDialogOpen]
  );

  const showStoreChangeDialog = useCallback(
    (newStore: IRestaurant, callback: () => void, selectedServiceMode: ServiceMode | null) => {
      const changeStoreMessage = t('changeStoreMessage', {
        serviceMode: selectedServiceMode && getLocalizedServiceModeCategory(selectedServiceMode),
      });

      setTimeout(() => {
        openStoreChangeDialog({
          newStore,
          callback,
          message: changeStoreMessage,
        });
        hasShownStoreChangeDialog.current = true;
      }, TRANSITION_ANIMATION_TIME_MS);
    },
    [openStoreChangeDialog]
  );

  const handleStoreChange = useCallback(
    async ({
      newStore,
      callback,
      hasCartItems,
      unavailableCartEntries,
      skipChangeStoreMessage,
      selectedServiceMode,
    }: {
      newStore: IRestaurant;
      callback: () => void;
      hasCartItems: boolean;
      unavailableCartEntries?: ILegacyCartEntry[];
      skipChangeStoreMessage: boolean;
      selectedServiceMode: ServiceMode | null;
    }) => {
      // No cart items - change immediately
      if (!hasCartItems) {
        return onConfirmStoreChange({ newStore, callback });
      }

      if (hasShownStoreChangeDialog.current || skipChangeStoreMessage) {
        return onConfirmStoreChange({ newStore, callback });
      }

      // Show store change dialog
      showStoreChangeDialog(newStore, callback, selectedServiceMode);
    },
    [onConfirmStoreChange, showStoreChangeDialog]
  );

  const prepareStoreData = useCallback(
    async (sanityStore: IRestaurant, requestedServiceMode?: ServiceMode) => {
      // Operating hours now come from our gql layer so we must merge the results
      // with the sanity store.
      // Default to the passed in sanity store if we cannot get a restaurant from
      // the gql layer.
      const rbiRestaurant = await getRestaurant(sanityStore.number ?? null);
      const newStore = rbiRestaurant
        ? mergeRestaurantData({ rbiRestaurant: rbiRestaurant as IRestaurant, sanityStore })
        : sanityStore;
      const selectedServiceMode = requestedServiceMode ?? serviceModeRef.current;

      return { newStore, selectedServiceMode, rbiRestaurant };
    },
    [getRestaurant]
  );

  const validateAndPrepareStore = useCallback(
    async (sanityStore: IRestaurant, requestedServiceMode?: ServiceMode) => {
      const { newStore, selectedServiceMode } = await prepareStoreData(
        sanityStore,
        requestedServiceMode
      );
      const validationResult = await validateStore(newStore, selectedServiceMode);
      return { newStore, selectedServiceMode, validationResult };
    },
    [prepareStoreData, validateStore]
  );

  /**
   * This function is used to orchestrate the store selection process.
   * if there is an error with the store while selecting, it calls the handleStoreError otherwise it calls the handleStoreChange
   */
  const selectStore = useCallback(
    async (options: ISelectStoreOptions) => {
      setIsSelectingStore(true);
      try {
        const { newStore, selectedServiceMode, validationResult } = await validateAndPrepareStore(
          options.sanityStore,
          options.requestedServiceMode
        );

        if (!validationResult.isValid) {
          handleStoreError(validationResult.reason, options.callback);
          return;
        }

        if (
          isSameStoreAndServiceMode(store, newStore, selectedServiceMode, serviceModeRef.current)
        ) {
          return options.callback();
        }

        await handleStoreChange({
          ...options,
          newStore,
          selectedServiceMode,
          skipChangeStoreMessage: options.skipChangeStoreMessage ?? false,
        });
      } finally {
        setIsSelectingStore(false);
      }
    },
    [validateAndPrepareStore, handleStoreError, handleStoreChange, store]
  );

  const isStoreAvailable = useMemo(() => {
    return store._id && !isFetchingStore && !isSelectingStore
      ? isStoreAvailableFn(store, serviceMode, store.localTime)
      : false;
  }, [store, isStoreAvailableFn, serviceMode, isFetchingStore, isSelectingStore]);

  useEffectOnce(() => {
    // Hold early analytics events (e.g. APP_OPEN) until the store is confirmed by the backend.
    // If a store exists, wait for the fetch to complete so events carry fresh store data.
    // If not, flush immediately — there's no store data to wait for.
    const storeInit = store?.number ? fetchAndSetStore(store.number) : Promise.resolve();
    storeInit.finally(flushEventQueue);
  });

  const value = useMemoAll({
    email,
    isFetchingStore,
    isStoreAvailable,
    isStoreOrderUnavailableDialogOpen,
    setIsStoreOrderUnavailableDialogOpen,
    prices,
    isPosDataLoading,
    refetchPosData,
    selectStore,
    fetchStore: fetchAndSetStore,
    setStore,
    resetStore,
    getStoreStatusFlags,
    isStoreOpenAndAvailable,
    isStoreOpenAndUnavailable,
    isStoreClosed,
    noStoreSelected,
    store,
    onConfirmStoreChange,
    onConfirmStoreChangeParams,
    clearOnConfirmStoreChangeParams,
    isSelectingStore,
    setIsSelectingStore,
    StoreChangeDialog: StoreChangeDialog as any,
    availableDayParts,
    setAvailableDayParts,
    setOnConfirmStoreChangeParams,
  });

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export default StoreContext.Consumer;
